import { MAX_RECOMMENDED_PRODUCTS, type PublicProductSummary } from '@repo/api-contracts';
import useEmblaCarousel from 'embla-carousel-react';
import { Plus } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ProductCard } from '@/entities/product';
import { STORE_PATH } from '@/shared/config';
import { HomeBestsellersControls } from './home-bestsellers-controls';

/** 데스크톱 한 화면에 보이는 카드 수 — 모자라면 빈 사진 칸으로 이 줄을 채운다. */
const VISIBLE_COUNT = 4;

interface HomeBestsellersProps {
  readonly products: readonly PublicProductSummary[];
}

interface SlideState {
  readonly pageCount: number;
  readonly pageIndex: number;
  readonly canPrev: boolean;
  readonly canNext: boolean;
}

/**
 * HOME 추천 상품 — 작은 영문 머리말 위 굵은 제목과 오른쪽 테두리 알약 "전체 보기 +",
 * 그 아래 각진 회색 사진 카드 최대 여덟 장을 한 화면에 네 장씩(태블릿 두 장) 가로로 넘긴다.
 * 넘길 묶음이 둘 이상이면 아래에 진행 막대와 이전·다음을 둔다. 끌어서 넘길 수도 있다.
 * 상품이 네 장보다 적으면 지어내지 않고 빈 사진 칸으로 한 줄을 채운다.
 */
export const HomeBestsellers = ({ products }: HomeBestsellersProps): ReactElement => {
  const visible = products.slice(0, MAX_RECOMMENDED_PRODUCTS);
  const placeholders = Array.from(
    { length: Math.max(VISIBLE_COUNT - visible.length, 0) },
    (_, index) => `empty-${index}`,
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 'auto',
    containScroll: 'trimSnaps',
  });
  const [slide, setSlide] = useState<SlideState>({
    pageCount: 1,
    pageIndex: 0,
    canPrev: false,
    canNext: false,
  });

  useEffect(() => {
    if (emblaApi === undefined) return undefined;
    const sync = (): void =>
      setSlide({
        pageCount: emblaApi.scrollSnapList().length,
        pageIndex: emblaApi.selectedScrollSnap(),
        canPrev: emblaApi.canScrollPrev(),
        canNext: emblaApi.canScrollNext(),
      });
    sync();
    emblaApi.on('select', sync).on('reInit', sync);
    return () => {
      emblaApi.off('select', sync).off('reInit', sync);
    };
  }, [emblaApi]);

  return (
    <section
      id="home-bestsellers"
      aria-labelledby="home-bestsellers-heading"
      className="w-full scroll-mt-20 px-5 sm:px-8 xl:px-14"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-base text-muted-foreground sm:text-lg">Recommended</p>
          <h2
            id="home-bestsellers-heading"
            className="text-[2rem] leading-tight font-bold tracking-[-0.03em] sm:text-[2.75rem]"
          >
            추천 상품
          </h2>
        </div>
        <Link
          to={STORE_PATH}
          className="inline-flex h-12 items-center gap-2 self-start rounded-full border border-foreground px-6 text-[15px] font-medium transition-colors hover:bg-foreground hover:text-background sm:self-auto"
        >
          전체 보기
          <Plus aria-hidden strokeWidth={1.75} className="size-4" />
        </Link>
      </div>

      {/* biome-ignore lint/a11y/useSemanticElements: fieldset은 캐러셀 뷰포트에 맞지 않는다. */}
      <div
        ref={emblaRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="추천 상품 목록"
        className="mt-10 overflow-hidden sm:mt-12"
      >
        <ul className="-ml-4 flex touch-pan-y touch-pinch-zoom">
          {visible.map((product, index) => (
            <li
              key={product.id}
              className="min-w-0 shrink-0 grow-0 basis-[78%] pl-4 sm:basis-1/2 lg:basis-1/4"
            >
              {/* 추천 순서(1부터)를 사진 왼쪽 위 표식으로 단다 — 카드는 스토어 목록과 같은 entities 카드다. */}
              <ProductCard product={product} badge={`PICK ${index + 1}`} />
            </li>
          ))}
          {placeholders.map((key) => (
            <li
              key={key}
              aria-hidden
              className="min-w-0 shrink-0 grow-0 basis-[78%] pl-4 sm:basis-1/2 lg:basis-1/4"
            >
              <div className="aspect-[4/5] w-full bg-muted/70" />
            </li>
          ))}
        </ul>
      </div>

      {slide.pageCount > 1 ? (
        <HomeBestsellersControls
          pageCount={slide.pageCount}
          pageIndex={slide.pageIndex}
          canPrev={slide.canPrev}
          canNext={slide.canNext}
          onPrev={() => emblaApi?.scrollPrev()}
          onNext={() => emblaApi?.scrollNext()}
        />
      ) : null}
    </section>
  );
};
